<script setup lang="ts">
import { computed, useSlots } from "vue";
import { RouterLink, useRouter } from "vue-router";

import LeDictionaryCover from "@/components/dictionaries/LeDictionaryCover.vue";
import LeDictionaryFavoriteToggle from "@/components/dictionary-selector/LeDictionaryFavoriteToggle.vue";
import { SearchFilterHelper, SearchRouteHelper } from "@/helpers";
import { useUserStore } from "@/stores";
import type { Dictionary } from "@/types";

interface Props {
  dictionary: Dictionary;
  hasResult?: boolean;
  isSelected?: boolean;
  isFavorite?: boolean;
  showFavoriteToggle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hasResult: false,
  isSelected: false,
  isFavorite: false,
  showFavoriteToggle: false
});

const emit = defineEmits<{
  (event: "select", dictionaryId: string): void;
  (event: "favorite", dictionaryId: string): void;
  (event: "unfavorite", dictionaryId: string): void;
}>();

const slots = useSlots();

const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);

const hasLanguageDecorator = computed<boolean>(() => slots.languages !== undefined);

const showFavoriteToggle = computed<boolean>(() => {
  if (hasLanguageDecorator.value) {
    return false;
  }

  return props.showFavoriteToggle;
});

const onSelect = (event: MouseEvent) => {
  // Enable the user to use browser shortcuts
  if (event.ctrlKey || event.metaKey) {
    return;
  }

  event.preventDefault();
  emit("select", props.dictionary.id);
};
</script>

<template>
  <div class="le-dictionaries-selector-item" :class="{ 'le-dictionaries-selector-item--selected': props.isSelected }">
    <RouterLink
      v-slot="{ href }"
      :to="searchRouteHelper.generateDictionarySearchUrl(props.dictionary.id)"
      :custom="true"
    >
      <a class="le-dictionaries-selector-item__button" :href="href" @click="onSelect($event)">
        <span
          class="le-dictionaries-selector-item__button-content"
          :class="{ 'le-dictionaries-selector-item__button-content--with-language-decorator': hasLanguageDecorator }"
        >
          <span
            class="le-dictionaries-selector-item__result-indicator"
            :class="{ 'le-dictionaries-selector-item__result-indicator--active': props.hasResult }"
          ></span>
          <span class="le-dictionaries-selector-item__cover">
            <LeDictionaryCover :dictionary="props.dictionary" size="medium" />
          </span>
          <span class="le-dictionaries-selector-item__title">{{ props.dictionary.title }}</span>
          <span class="le-dictionaries-selector-item__publisher">{{ props.dictionary.publisher }}</span>
          <span v-if="hasLanguageDecorator" class="le-dictionaries-selector-item__languages">
            <slot name="languages" :search-filter="SearchFilterHelper.convertToSearchFilter(props.dictionary)" />
          </span>
        </span>
      </a>
    </RouterLink>

    <LeDictionaryFavoriteToggle
      v-if="showFavoriteToggle"
      :is-favorite="props.isFavorite"
      class="le-dictionaries-selector-item__favorite-toggle"
      @favorite="emit('favorite', props.dictionary.id)"
      @unfavorite="emit('unfavorite', props.dictionary.id)"
    />
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.le-dictionaries-selector-item {
  position: relative;
  transition: background-color 75ms ease-out;
  border-radius: 4px;

  &:hover {
    background-color: @colors[@secondary][200];
  }

  &--selected {
    background-color: @colors[@accent][300];

    &:hover {
      background-color: @colors[@accent][400];
    }
  }

  &__button {
    display: block;
    width: 100%;
    margin: 0;
    padding: 6px;
    border: 0;
    border-radius: 4px;
    background: none;
    text-align: left;

    &.focus-visible {
      .focus-outline(green);
    }
  }

  &--selected &__button.focus-visible {
    .focus-outline(yellow);
  }

  &__button-content {
    display: grid;
    grid-auto-rows: 18px;
    grid-template-areas:
      "result-indicator cover title languages"
      "result-indicator cover publisher languages";
    grid-template-columns: 4px auto minmax(0, 1fr) auto auto;
    align-items: center;
    padding-right: 32px;
    column-gap: 4px;

    &--with-language-decorator {
      padding-right: 0;
    }
  }

  &__result-indicator {
    grid-area: result-indicator;
    width: 4px;
    height: 100%;
    border-radius: 2px;
    opacity: 0.3;
    background-color: @colors[@secondary][600];

    &--active {
      opacity: 1;
      background-color: @colors[@accent][500];
    }
  }

  &__cover {
    grid-area: cover;
    margin-right: 8px;
    background-color: @colors[@secondary][300];
  }

  &__title {
    grid-area: title;
    overflow: hidden;
    color: @colors[@neutrals][400];
    font-family: @font-family-body;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    white-space: nowrap;
    mask-image: linear-gradient(to right, black calc(100% - 30px), transparent 100%);
  }

  &__publisher {
    grid-area: publisher;
    overflow: hidden;
    color: @colors[@neutrals][300];
    font-family: @font-family-body;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    white-space: nowrap;
    mask-image: linear-gradient(to right, black calc(100% - 30px), transparent 100%);
  }

  &__languages {
    grid-area: languages;
  }

  &__favorite-toggle {
    position: absolute;
    z-index: @le-dictionary-selector-item__favorite-toggle-z-index;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);
  }
}
</style>
